import React, { useEffect, useState } from "react";
import NavBar from "./NavBar";
import Footer from "./Footer";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { InfoIcon, Plus } from "lucide-react";
import InputPesquisa from "../components/Inputs/InputPesquisa";
import Loading from "./Loading";
import { motion } from "framer-motion";
import Swal from "sweetalert2";

const API_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:8002"
    : "https://api-eyazs-production-8f74.up.railway.app");

export default function ClientesDesativos() {
  const [usuarios, setUsuarios] = useState([]);
  const [pesquisa, setPesquisa] = useState([]);
  const [usuario, setUsuario] = useState("");
  const [loading, setLoading] = useState(false);
  const [acaoClienteId, setAcaoClienteId] = useState(null);
  const navigate = useNavigate();

  function planoParaAdmin(cliente) {
    return cliente?.name?.trim().toLowerCase() === "adanca"
      ? cliente.profileusuario || "-"
      : cliente.profile || "-";
  }

  function getHeaders() {
    const token = JSON.parse(localStorage.getItem("token"));
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }

  function escaparHtml(valor) {
    return String(valor || "").replace(/[&<>"']/g, (caractere) => {
      const mapa = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      };
      return mapa[caractere];
    });
  }

  async function mostrarUsuarios() {
    try {
      setLoading(true);
      const dados = await axios.get(
        `${API_URL}/admin/clientes`,
        getHeaders()
      );
      if (dados) {
        const usuariosDesativos = dados.data
          .filter(
            (cliente) =>
              cliente.profile !== "CONGELADO" &&
              (cliente.profile === "DESATVADO" || cliente.disabled === "true")
          )
          .sort((a, b) => a.name.localeCompare(b.name));
        setUsuarios(usuariosDesativos);
      }
    } catch (erro) {
      console.log(erro);
    } finally {
      setLoading(false);
    }
  }

  function mostrarPesquisa() {
    const termo = usuario.toLowerCase();
    const dadosFiltrados = usuarios.filter((cliente) =>
      cliente.name?.toLowerCase().includes(termo)
    );
    setPesquisa(dadosFiltrados);
  }

  async function adicionarDiasCliente(cliente) {
    const planoPadrao = escaparHtml(planoParaAdmin(cliente) === "-" ? "" : planoParaAdmin(cliente));

    const resultado = await Swal.fire({
      title: `Adicionar dias para ${cliente.name}`,
      html: `
        <div style="display:flex;flex-direction:column;gap:10px;text-align:left">
          <label>Dias</label>
          <input id="diasCliente" type="number" min="1" value="31" class="swal2-input" style="margin:0;width:100%" />
          <label>Plano</label>
          <input id="planoCliente" value="${planoPadrao}" class="swal2-input" style="margin:0;width:100%" />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Adicionar dias",
      cancelButtonText: "Cancelar",
      focusConfirm: false,
      preConfirm: () => {
        const dias = Number(document.getElementById("diasCliente").value);
        const plano = document.getElementById("planoCliente").value.trim();

        if (!Number.isInteger(dias) || dias <= 0) {
          Swal.showValidationMessage("Informe uma quantidade de dias valida.");
          return false;
        }

        if (!plano) {
          Swal.showValidationMessage("Nao foi possivel identificar o plano atual do cliente.");
          return false;
        }

        return {
          dias,
          plano,
          criarRecibo: false,
        };
      },
    });

    if (!resultado.isConfirmed) return;

    try {
      setAcaoClienteId(cliente.idUsuario);
      const response = await axios.put(
        `${API_URL}/admin/cliente/${cliente.idUsuario}/adicionar-dias`,
        resultado.value,
        getHeaders()
      );
      await mostrarUsuarios();
      await Swal.fire({
        title: "Concluido",
        text: response.data?.mensagem || "Dias adicionados com sucesso.",
        icon: "success",
      });
    } catch (erro) {
      Swal.fire({
        title: "Erro",
        text: erro.response?.data?.mensagem || "Nao foi possivel adicionar dias.",
        icon: "error",
      });
    } finally {
      setAcaoClienteId(null);
    }
  }

  useEffect(() => {
    if (usuario) mostrarPesquisa();
    else setPesquisa([]);
  }, [usuario, usuarios]);

  useEffect(() => {
    mostrarUsuarios();
  }, []);

  const propsInputPesquisa = {
    type: "search",
    mudar: (e) => setUsuario(e.target.value),
    placeholder: "pesquise o usuário",
    value: usuario,
  };

  const dadosExibidos = pesquisa.length > 0 ? pesquisa : usuarios;

  return (
    <div className="flex justify-between flex-col min-h-screen">
      {/* Navbar */}
      <div className="bg-blue-500 sticky top-0 z-10">
        <NavBar />
      </div>

      {/* Conteúdo animado */}
      {loading ? (
        <Loading />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col items-center px-4 py-8 z-0"
        >
          <div className="w-full max-w-5xl flex flex-col gap-4">
            <InputPesquisa {...propsInputPesquisa} />

            <div className="border w-full overflow-auto rounded-md">
              <table className="min-w-full text-sm text-left text-gray-500">
                <thead className="bg-gray-200 sticky top-0">
                  <tr>
                    <th className="border px-4 py-2 text-center">Ordem</th>
                    <th className="border px-4 py-2 text-center">Nome</th>
                    <th className="border px-4 py-2 text-center">Plano</th>
                    <th className="border px-4 py-2 text-center">Serviço</th>
                    <th className="border px-4 py-2 text-center">Info. Cliente</th>
                    <th className="border px-4 py-2 text-center">Recibos</th>
                    <th className="border px-4 py-2 text-center">Adicionar</th>
                  </tr>
                </thead>

                <motion.tbody
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: { transition: { staggerChildren: 0.05 } },
                  }}
                >
                  {dadosExibidos.map((usuario, i) => (
                    <motion.tr
                      key={i}
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        visible: { opacity: 1, y: 0 },
                      }}
                      className="hover:bg-gray-100"
                    >
                      <td className="border px-4 py-2 text-center">{i + 1}</td>
                      <td className="border px-4 py-2 text-center">{usuario.name}</td>
                      <td className="border px-4 py-2 text-center">{planoParaAdmin(usuario)}</td>
                      <td className="border px-4 py-2 text-center">{usuario.service}</td>
                      <td className="border px-4 py-2 text-center">
                        <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
                          <InfoIcon
                            size={22}
                            className="mx-auto cursor-pointer hover:text-blue-500"
                            onClick={() => navigate(`detalhesusuario/${usuario.idUsuario}`)}
                          />
                        </motion.div>
                      </td>
                      <td className="border px-4 py-2 text-center">
                        <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-6 h-6 mx-auto cursor-pointer hover:text-blue-500"
                            onClick={() => navigate(`recibosusuario/${usuario.idUsuario}`)}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m.75 12 3 3m0 0 3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                            />
                          </svg>
                        </motion.div>
                      </td>
                      <td className="border px-4 py-2 text-center">
                        <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
                          <Plus
                            className={`mx-auto cursor-pointer hover:text-blue-500 ${
                              acaoClienteId === usuario.idUsuario ? "opacity-50" : ""
                            }`}
                            onClick={() =>
                              acaoClienteId === usuario.idUsuario
                                ? null
                                : adicionarDiasCliente(usuario)
                            }
                          />
                        </motion.div>
                      </td>
                    </motion.tr>
                  ))}
                </motion.tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Footer */}
      <div className="bg-blue-500 w-full">
        <Footer />
      </div>
    </div>
  );
}
